import clsx from 'clsx'
import {FC} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'

type Props = {
  show: boolean
  onHide: () => void
  /** "Ask Versi" — opens the assistant. */
  onAskVersi: () => void
  /** "Create a support ticket" — goes to the ticket form. */
  onCreateTicket: () => void
}

type OptionProps = {
  icon: string
  titleId: string
  descriptionId: string
  recommended?: boolean
  /** Shown as already selected (blue), so the recommended option is the obvious pick. */
  selected?: boolean
  onClick: () => void
}

const SupportChoiceOption: FC<OptionProps> = ({
  icon,
  titleId,
  descriptionId,
  recommended = false,
  selected = false,
  onClick,
}) => {
  const intl = useIntl()

  return (
    <button
      type='button'
      onClick={onClick}
      // Enter on open picks the recommended option.
      autoFocus={selected}
      className={clsx(
        'd-flex align-items-start gap-4 w-100 text-start rounded p-5 border border-2',
        selected
          ? 'border-primary'
          : 'border-gray-300 bg-transparent bg-hover-light-primary border-hover-primary'
      )}
      // bg-light-primary (#F1FAFF) is too pale to read as selected; use a visible primary tint.
      style={selected ? {backgroundColor: 'rgba(var(--bs-primary-rgb), 0.08)'} : undefined}
    >
      <span
        className={clsx(
          'd-flex align-items-center justify-content-center rounded-circle flex-shrink-0 w-45px h-45px',
          selected ? 'bg-primary' : 'bg-light-primary'
        )}
      >
        <i
          className={clsx('fa-solid fs-3', icon, selected ? 'text-white' : 'text-primary')}
          aria-hidden='true'
        />
      </span>
      <span className='d-flex flex-column flex-grow-1'>
        <span className='d-flex align-items-center flex-wrap gap-2 mb-1'>
          <span className='fw-bold fs-5 text-gray-900'>{intl.formatMessage({id: titleId})}</span>
          {recommended && (
            <span className='badge badge-light-success fw-semibold'>
              {intl.formatMessage({id: 'TICKET.SUPPORT_CHOICE.RECOMMENDED'})}
            </span>
          )}
        </span>
        <span className='text-gray-600 fs-7'>{intl.formatMessage({id: descriptionId})}</span>
      </span>
    </button>
  )
}

/**
 * "Need Any Support?" chooser: ask Versi (recommended — instant, answers most questions) or
 * create a support ticket for the team. Versi shows as selected; clicking a card acts on it
 * straight away. Opened by useSupportEntry.
 */
const SupportChoiceModal: FC<Props> = ({show, onHide, onAskVersi, onCreateTicket}) => {
  const intl = useIntl()

  return (
    <Modal show={show} onHide={onHide} centered dialogClassName='mw-550px'>
      <Modal.Header closeButton>
        <div>
          <Modal.Title as='h3' className='mb-1'>
            {intl.formatMessage({id: 'TICKET.SUPPORT_CHOICE.TITLE'})}
          </Modal.Title>
          <div className='text-gray-600 fs-7'>
            {intl.formatMessage({id: 'TICKET.SUPPORT_CHOICE.SUBTITLE'})}
          </div>
        </div>
      </Modal.Header>
      <Modal.Body className='d-flex flex-column gap-4'>
        <SupportChoiceOption
          icon='fa-wand-magic-sparkles'
          titleId='TICKET.SUPPORT_CHOICE.VERSI.TITLE'
          descriptionId='TICKET.SUPPORT_CHOICE.VERSI.DESCRIPTION'
          recommended
          selected
          onClick={onAskVersi}
        />
        <SupportChoiceOption
          icon='fa-ticket'
          titleId='TICKET.SUPPORT_CHOICE.TICKET.TITLE'
          descriptionId='TICKET.SUPPORT_CHOICE.TICKET.DESCRIPTION'
          onClick={onCreateTicket}
        />
        <div className='d-flex align-items-start gap-2 text-gray-600 fs-7'>
          <i className='fa-solid fa-circle-info text-primary mt-1' aria-hidden='true' />
          <span>{intl.formatMessage({id: 'TICKET.SUPPORT_CHOICE.HINT'})}</span>
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default SupportChoiceModal
